import { api } from "./client";
import type { CreationOptionsJson } from "../shared/webauthn";
import type {
  PasskeyOut,
  ConsentOut,
  IdentityOut,
  McpClientOut,
  OrgOut,
  SessionOut,
  TokenOut,
  UserOut,
  MyProduct,
} from "./types";

export const meApi = {
  get: () => api<UserOut>("/me"),
  patch: (body: {
    first_name?: string;
    last_name?: string;
    locale?: string;
    country?: string;
  }) => api<UserOut>("/me", { method: "PATCH", body }),
  setActiveOrg: (org_id: string) =>
    api<UserOut>("/me/active-org", { method: "PUT", body: { org_id } }),
  sessions: () => api<SessionOut[]>("/me/sessions"),
  revokeSession: (id: string) =>
    api<void>(`/me/sessions/${id}`, { method: "DELETE" }),
  revokeOtherSessions: () => api<void>("/me/sessions", { method: "DELETE" }),
  identities: () => api<IdentityOut[]>("/me/identities"),
  unlinkIdentity: (provider: string) =>
    api<void>(`/me/identities/${provider}`, { method: "DELETE" }),
  changePassword: (body: { current_password?: string; new_password: string }) =>
    api<{ changed: boolean }>("/me/password", { method: "PUT", body }),
  tokens: () => api<TokenOut[]>("/me/tokens"),
  createToken: (body: { product: string; label: string; scopes: string[] }) =>
    api<TokenOut>("/me/tokens", { method: "POST", body }),
  revokeToken: (id: string) =>
    api<void>(`/me/tokens/${id}`, { method: "DELETE" }),
  clients: () => api<McpClientOut[]>("/me/clients"),
  createClient: (body: {
    name: string;
    redirect_uris: string[];
    product?: string;
  }) => api<McpClientOut>("/me/clients", { method: "POST", body }),
  disableClient: (id: string) =>
    api<void>(`/me/clients/${id}`, { method: "DELETE" }),
  orgs: () => api<OrgOut[]>("/me/orgs"),
  products: () => api<MyProduct[]>("/me/products"),
  createOrg: (name: string) =>
    api<OrgOut>("/me/orgs", { method: "POST", body: { name } }),
  addMember: (orgId: string, body: { email: string; role: string }) =>
    api<{ user_id: string; email: string; role: string }>(
      `/me/orgs/${orgId}/members`,
      { method: "POST", body },
    ),
  removeMember: (orgId: string, userId: string) =>
    api<void>(`/me/orgs/${orgId}/members/${userId}`, { method: "DELETE" }),
  consents: () => api<ConsentOut[]>("/me/consents"),
  revokeConsent: (clientId: string) =>
    api<void>(`/me/consents/${encodeURIComponent(clientId)}`, {
      method: "DELETE",
    }),
  deleteAccount: (body: { password?: string; confirm?: string }) =>
    api<void>("/me/delete", { method: "POST", body }),
  exportUrl: () => "/me/export",
  mfaSetup: () =>
    api<{ secret: string; otpauth_uri: string; qr_data_url: string }>(
      "/me/mfa/totp/setup",
      { method: "POST" },
    ),
  mfaConfirm: (code: string) =>
    api<{ enabled: boolean; recovery_codes: string[] }>(
      "/me/mfa/totp/confirm",
      {
        method: "POST",
        body: { code },
      },
    ),
  mfaDisable: (body: { code?: string; password?: string }) =>
    api<void>("/me/mfa/totp", { method: "DELETE", body }),
  passkeys: () => api<PasskeyOut[]>("/me/passkeys"),
  passkeyOptions: () =>
    api<CreationOptionsJson>("/me/passkeys/options", { method: "POST" }),
  addPasskey: (body: { name: string; credential: Record<string, unknown> }) =>
    api<PasskeyOut>("/me/passkeys", { method: "POST", body }),
  removePasskey: (id: string) =>
    api<void>(`/me/passkeys/${id}`, { method: "DELETE" }),
  mfaRecoveryCodes: (code: string) =>
    api<{ recovery_codes: string[] }>("/me/mfa/recovery-codes", {
      method: "POST",
      body: { code },
    }),
};
